import React, { useEffect, useState } from 'react';
import Api from '../../Api';
import { withAuth } from '../../hoc/Auth';
import { Column, Container, Wrapper } from '../../hoc/Layout';
import ResponsiveSidebarFilters from '../../Components/Utils/ResponsiveSidebarFilters';
import AdminFiltersToggle from '../../Components/ComponentsOfAdministrator/AdminFiltersToggle';
import { FormattedMessage } from 'react-intl';
import Icon from '../../Components/Utils/Icon';
import Pagination from 'react-js-pagination';
import DateRange from '../../Components/Utils/DateRange';

import EmptyCard from '../../hoc/EmptyCard';
import Search from '../../Components/Utils/SearchTags';

import TerminalsList from '../../Components/ComponentsOfAdministrator/TerminalsList';
import TerminalAddWizard from '../../Components/TerminalAddWizard';
import TerminalsFilters from '../../Components/ComponentsOfAdministrator/TerminalsFilters';
import TerminalDetailsTabs from './TerminalDetailsTabs';

import classes from './styles.module.css';
import filtersClasses from '../../Components/ComponentsOfAdministrator/AdminFiltersToggle/styles.module.css';

const TerminalsPage = withAuth(function ({ token }) {
    // modal
    const [isWizardOpen, setIsWizardOpen] = useState(false);

    // paging
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // data
    const [list, setList] = useState([]);
    const [selected, setSelected] = useState(null); // { id, projectUuid, ... }

    // filters & search & dates
    const [sort] = useState('DESC');
    const [filterDate, setFilterDate] = useState({});
    const [searchFilters, setSearchFilters] = useState([]);
    const [sidebarFilters, setSidebarFilters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    // load list
    useEffect(() => {
        const filters = [...searchFilters, ...sidebarFilters];
        const payload = {
            page: currentPage,
            itemsPerPage: perPage,
            order: sort,
            dateRange: filterDate,
            filters: [...filters],
        };

        setLoading(true);
        new Api({ token }).getTerminalsList(payload).then((r) => {
            setList(Array.isArray(r.data) ? r.data : []);
            setTotalCount(typeof r.count === 'number' ? r.count : 0);
            setLoading(false);
        });
    }, [searchFilters, sidebarFilters, filterDate, currentPage, perPage, sort, token]);

    function setPage(page, itemsPerPage) {
        setCurrentPage(page || 1);
        if (itemsPerPage) setPerPage(itemsPerPage);
    }

    function onChangeSearch(tags) {
        setSearchFilters(Array.isArray(tags) ? tags : []);
    }

    function onSelectTerminal(row) {
        if (!row || !row.id) {
            setSelected(null);
            return;
        }
        // Список приходит с урезанным набором полей; терминал-детали
        // (включая capabilities) — отдельным эндпоинтом. Объединяем,
        // чтобы дочерние компоненты сразу получили capabilities и не
        // показывали методы, недоступные для protocol'а.
        setSelected({ ...row, _detailLoading: true });
        new Api({ token })
            .getTerminalData(row.id)
            .then((detail) => setSelected({ ...row, ...detail, _detailLoading: false }))
            .catch(() => setSelected({ ...row, _detailLoading: false }));
    }

    return (
        <React.Fragment>
            {isWizardOpen && (
                <TerminalAddWizard
                    token={token}
                    onClose={function () {
                        setIsWizardOpen(false);
                    }}
                    onCreated={function () {
                        setIsWizardOpen(false);
                        setCurrentPage(1);
                        setSidebarFilters((prev) => [].concat(prev));
                    }}
                />
            )}
            <Wrapper>
                <Container>
                    <Column width="780" className={filtersClasses.MainColumn}>
                        <AdminFiltersToggle onClick={() => setIsFiltersOpen(true)} />
                        <Search
                            onChangeSearch={onChangeSearch}
                            onSearch={() => {
                                setSearchFilters((prev) => [...prev]);
                                setCurrentPage(1);
                            }}
                            isDisabled={false}
                            defaultSearch={{
                                key: 'merchantUuid',
                                locale: 'Search.label.projectUuid',
                                placeholder: 'Search.placeholder.projectUuid',
                            }}
                            searchBy={[
                                {
                                    key: 'merchantUuid',
                                    locale: 'Search.label.projectUuid',
                                    placeholder: 'Search.placeholder.projectUuid',
                                },
                                {
                                    key: 'merchantName',
                                    locale: 'Search.label.projectName',
                                    placeholder: 'Search.placeholder.projectName',
                                },
                                {
                                    key: 'terminalId',
                                    locale: 'Search.label.terminalId',
                                    placeholder: 'Search.placeholder.terminalId',
                                },
                                {
                                    key: 'terminalName',
                                    locale: 'Search.label.terminalName',
                                    placeholder: 'Search.placeholder.terminalName',
                                },
                                {
                                    key: 'providerName',
                                    locale: 'Search.label.providerName',
                                    placeholder: 'Search.placeholder.providerName',
                                },
                                {
                                    key: 'currencyCode',
                                    locale: 'Search.label.currency',
                                    placeholder: 'Search.placeholder.currency',
                                },
                            ]}
                        />

                        <div className={classes.Card}>
                            <div className={classes.Header}>
                                <h5 className={classes.Title}>
                                    <FormattedMessage id="Terminals.title" defaultMessage="Терминалы" />
                                </h5>

                                <div className={classes.Btn} onClick={() => setIsWizardOpen(true)}>
                                    <Icon className={classes.IconLeft} icon="add-outline" size={'16px'} />
                                    <FormattedMessage id="Terminals.add" defaultMessage="Добавить" />
                                </div>
                            </div>

                            <TerminalsList
                                isLoading={loading}
                                data={list}
                                currentId={selected ? selected.id : null}
                                onSelect={onSelectTerminal}
                            />

                            {perPage < totalCount && (
                                <div className={classes.PaginationWrap}>
                                    <Pagination
                                        firstPageText="⇤"
                                        prevPageText="⇠"
                                        lastPageText="⇥"
                                        nextPageText="⇢"
                                        activeLinkClass={classes.ActiveLink}
                                        activePage={currentPage}
                                        itemsCountPerPage={perPage}
                                        totalItemsCount={totalCount}
                                        pageRangeDisplayed={5}
                                        onChange={(page) => setPage(page, perPage)}
                                    />
                                </div>
                            )}
                        </div>

                        <TerminalDetailsTabs
                            token={token}
                            terminal={selected}
                            onSaved={() => {
                                setCurrentPage(1);
                            }}
                        />
                    </Column>

                    <Column width="350" className={filtersClasses.SidebarColumn}>
                        <ResponsiveSidebarFilters
                            title="Фильтры"
                            isOpen={isFiltersOpen}
                            onClose={() => setIsFiltersOpen(false)}
                        >
                            <EmptyCard>
                                <DateRange
                                    onSelectDate={(range) => {
                                        setFilterDate(range || {});
                                        setCurrentPage(1);
                                    }}
                                    defaultPeriod={'zero'}
                                />
                            </EmptyCard>

                            <TerminalsFilters
                                isLoading={loading}
                                token={token}
                                onApplyFilters={(newFilters) => {
                                    setSidebarFilters(Array.isArray(newFilters) ? newFilters : []);
                                    setCurrentPage(1);
                                    setIsFiltersOpen(false);
                                }}
                            />
                        </ResponsiveSidebarFilters>
                    </Column>
                </Container>
            </Wrapper>
        </React.Fragment>
    );
});

export default TerminalsPage;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/Terminals/index.js